import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { decodeImpersonationPayload, normalizeAdminUser, normalizeMerchantUser } from '../../utils/authMappers';
import { apiService } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import { resolveMerchantDashboardKey } from '../../utils/merchantDashboardKey';
import Loader from '../shared/Loader';

/**
 * Lands in a brand-new browser tab opened by UserManagement's "Login as" action. The tab's
 * authStore is already scoped to sessionStorage (see store/authStore.ts isImpersonationTab check),
 * so signing in here never touches the admin's own session in their original tab.
 */
const ImpersonateBridge: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [error, setError] = useState('');
  // The effect below reads-then-strips window.location.hash, so it isn't idempotent. React
  // StrictMode double-invokes effects in dev, which made the 2nd run see an already-stripped
  // hash and misreport a missing session. This ref makes the bootstrap run exactly once.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const hash = window.location.hash;
    const prefix = '#impersonate=';

    if (!hash.startsWith(prefix)) {
      setError('Missing impersonation session data.');
      return;
    }

    try {
      const encoded = hash.slice(prefix.length);
      const decoded = decodeImpersonationPayload(encoded);

      // Strip the hash immediately so the token doesn't linger in this tab's history.
      window.history.replaceState(null, '', window.location.pathname);

      const { token, ...sessionData } = decoded;
      if (!token) {
        setError('Impersonation session is missing a token.');
        return;
      }

      // adminId is only ever set for a referral-admin impersonation session (see
      // AdminServiceImpl#impersonateAdminUser / ImpersonationSessionResponse) — merchant
      // impersonation sets merchantId/merchantSubUserId instead. Routing both through
      // normalizeMerchantUser (the original, merchant-only version of this bridge) silently
      // mislabeled an impersonated referral admin as userType 'MERCHANT', which hid the referral
      // merchant-select dropdown and showed the wrong (module-access-based) sidebar entirely.
      const user = sessionData.adminId != null
        ? normalizeAdminUser(sessionData, sessionData.email)
        : normalizeMerchantUser(sessionData, sessionData.email);

      const applySession = async () => {
        if (user.userType === 'MERCHANT' && user.merchantId) {
          try {
            const res = await apiService.get(`${API_PATHS.GET_MERCHANT}/${user.merchantId}`);
            user.dashboardKey = resolveMerchantDashboardKey(res);
          } catch (error) {
            console.error('Failed to load impersonated merchant dashboard key:', error);
          }
        }
        login(user, token);
        navigate('/dashboard/transaction-dashboard', { replace: true });
      };

      void applySession();
    } catch (err) {
      console.error('Failed to bootstrap impersonation session:', err);
      setError('Failed to start impersonation session.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '1rem',
      background: '#0b1120',
      color: '#f1f5f9',
    }}>
      {error ? (
        <span>{error}</span>
      ) : (
        <Loader size="lg" tone="light" label="Signing in…" />
      )}
    </div>
  );
};

export default ImpersonateBridge;
